import { useEffect, useMemo, useState } from 'react';
import AdminUserPicker from './AdminUserPicker';
import { getAdminCcdApprovers, saveAdminCcdApprovers } from './workflow-api';

function emailsFromText(value) {
  return [...new Set(String(value || '').split(/[\n,;]+/u).map((item) => item.trim().toLowerCase()).filter(Boolean))];
}

function emailsText(view) {
  return (view?.approvers || []).map((approver) => approver.email).filter(Boolean).join('\n')
    || (view?.emails || []).join('\n');
}

export default function CentralCcdApproversPanel({ settings, getAccessToken, onDirtyChange, onBusyChange, externallyBusy = false }) {
  const canManage = settings?.permissions?.manageCcdApprovers === true;
  const writerReady = settings?.writeStatus === 'ready' && typeof getAccessToken === 'function' && !externallyBusy;
  const [view, setView] = useState(null);
  const [draftText, setDraftText] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const emails = useMemo(() => emailsFromText(draftText), [draftText]);
  const reasonReady = reason.trim().length >= 5 && reason.trim().length <= 500;
  const draftReady = emails.length >= 1 && emails.length <= 10;
  const dirty = Boolean(view && (draftText !== emailsText(view) || reason));

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { onBusyChange?.(loading || saving); }, [loading, saving, onBusyChange]);
  useEffect(() => {
    if (!dirty && !saving) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saving]);

  useEffect(() => {
    if (!canManage || !getAccessToken) return;
    let active = true;
    setLoading(true);
    setError('');
    getAdminCcdApprovers(getAccessToken)
      .then((result) => {
        if (!active) return;
        const next = result?.ccdApprovers || null;
        setView(next);
        setDraftText(emailsText(next));
      })
      .catch((loadError) => { if (active) setError(loadError instanceof Error ? loadError.message : String(loadError)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // AdminApp recreates getAccessToken while rendering; reloading on that function
    // identity would create a fetch loop. Reload only when CCD management becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);

  if (!canManage) return null;

  async function save() {
    if (!writerReady || !view || !draftReady || !reasonReady || saving) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const result = await saveAdminCcdApprovers(emails, view.version ?? null, reason, getAccessToken);
      const next = result?.ccdApprovers || null;
      setView(next);
      setDraftText(emailsText(next));
      setReason('');
      setMessage('Central CCD approvers saved and audited. Approval emails, reminders, dashboard access, and Outlook actions now use this list.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
    } finally {
      setSaving(false);
    }
  }

  return <section className="admin-settings-panel central-ccd-panel">
    <div className="admin-section-heading">
      <span>CCD</span>
      <div>
        <h4>Central CCD Approvers</h4>
        <small>One CCD approver list is shared by every company that requires CCD approval. Companies marked CCD Not Required bypass this stage after HC approval.</small>
      </div>
    </div>

    <section className="admin-settings-card">
      {loading && <p>Loading central CCD approvers…</p>}
      {!loading && view && <>
        <div className="admin-card-title-row">
          <h5>Central CCD source of truth</h5>
          <span className="admin-protected-badge">{view.source === 'admin-settings' ? 'Admin settings' : 'Environment fallback'}</span>
        </div>
        <p>{view.count || 0} central CCD approver(s) configured.</p>
        <AdminUserPicker
          value={emails}
          onChange={(values) => setDraftText(values.join('\n'))}
          getAccessToken={getAccessToken}
          disabled={!writerReady || saving}
          max={10}
          label="Central CCD approvers"
          placeholder="Search CCD approver by name or email"
          help="Search Microsoft Entra by name or email, then select the user. This list is the authoritative CCD permission source."
        />
        <div className="admin-callout">
          <strong>Authoritative list</strong>
          <span>This same list controls Pending CCD delivery, SLA reminders, dashboard visibility, and approval permission. A legacy CCD.Approver role by itself no longer grants CCD approval.</span>
        </div>
        <div className="admin-callout admin-callout-warm">
          <strong>Company exception</strong>
          <span>If a company is marked CCD Not Required, both photo update and photo removal go from HC approval directly to Microsoft 365 processing and never return to Central CCD.</span>
        </div>
        <label className="admin-change-reason">Change reason
          <textarea rows="2" maxLength="500" value={reason} disabled={!writerReady || saving} onChange={(event) => setReason(event.target.value)} placeholder="Why is the central CCD approver list changing?" />
        </label>
        {!draftReady && <small className="admin-validation-error">Central CCD requires 1–10 approvers.</small>}
        {reason.length > 0 && !reasonReady && <small className="admin-validation-error">Enter 5–500 characters for the audit reason.</small>}
        {message && <small className="admin-save-success">{message}</small>}
        {!writerReady && <small className="company-policy-lock-note">Read-only while Admin settings writes are locked.</small>}
        <button onClick={save} disabled={!writerReady || !draftReady || !reasonReady || saving}>{saving ? 'Saving…' : 'Save Central CCD'}</button>
      </>}
      {error && <small className="admin-validation-error">{error}</small>}
    </section>
  </section>;
}
